import { useMemo } from 'react'

import { useAtom } from 'jotai'
import { isNil } from 'ramda'

import { Loader } from '@twisto/components/atoms/loader'

import { submittingFlowFormAtom } from '../components'
import {
  AccountExistsWithoutPasswordStep,
  AddNewBankAccountStep,
  AdditionalInfoIntroStep,
  BankAccountConnectionErrorStep,
  BankAccountConnectionPartialStep,
  BankAccountConnectionProcessingStep,
  BankAccountConnectionTooLongStep,
  BankConnectionIntroStep,
  BankConnectionSuccessStep,
  CashAgreementsStep,
  CashBankAccountEligibilityStep,
  CashBankAccountsListStep,
  CashCompletedStep,
  CashInstallmentsOfferStep,
  ConsentCollectionCompletedStep,
  ConsentCollectionIntroStep,
  CountryOfBirthStep,
  CrossCheckWarningStep,
  CzBasicAccountAgreementsStep,
  EducationTypeStep,
  EmailPrivacyPolicyStep,
  EmploymentTypeStep,
  ExpensesStep,
  FamilyChildrenStep,
  FamilyStatusStep,
  GeneralErrorStep,
  IdentityVerificationIntroStep,
  IdentityVerificationRetryStep,
  IncomeStep,
  IncomeTypeStep,
  IncomeVerificationBankAccountConnectionErrorStep,
  IncomeVerificationBankAccountConnectionTooLongStep,
  IncomeVerificationBankAccountConnectionWarningStep,
  IncomeVerificationCompletedStep,
  IncomeVerificationIntroStep,
  InstallmentsAgreementsStep,
  InstallmentsDisclaimerStep,
  InternationalAddressStep,
  LoanRepaymentsStep,
  NameStep,
  OnboardingCompletedStep,
  OnboardingErrorStep,
  OnboardingRejectedStep,
  OtpVerificationEmailStep,
  OtpVerificationSmsStep,
  PaidPayIn3CompletedStep,
  PasswordStep,
  PersonalIdStep,
  PhoneNumberStep,
  PlBasicAccountAgreementsStep,
  PremiumAccountAgreementsStep,
  ProcessingStep,
  RedirectStep,
  ReviewIncomeStep,
  RiskApprovedStep,
  RiskCheckProcessingStep,
  RiskManualReviewStep,
  SelectCashAmountStep,
  SingleInstallmentsOfferStep,
} from '../views'

import type { FlowContextType } from './flow.context'
import { FlowContext } from './flow.context'
import { useGetFlowData } from './flow.hook'

export type FlowModalProps = {
  sessionId: string | null
  actions?: FlowContextType['actions']
}

export const Flow = ({ sessionId, actions = {} }: FlowModalProps) => {
  const { flow, loading } = useGetFlowData(sessionId)
  const [submitting] = useAtom(submittingFlowFormAtom)

  const flowApi = useMemo(
    () =>
      flow
        ? {
            flow,
            sessionId: flow.sessionId,
            actions,
          }
        : null,
    [flow, actions]
  )

  const step = flow?.currentStep.__typename

  const renderCurrentStep = () => {
    switch (step) {
      case 'AddNewBankAccountStep':
        return <AddNewBankAccountStep />
      case 'BankAccountConnectionErrorStep':
        return <BankAccountConnectionErrorStep />
      case 'BankAccountConnectionPartialStep':
        return <BankAccountConnectionPartialStep />
      case 'BankAccountConnectionProcessingStep':
        return <BankAccountConnectionProcessingStep />
      case 'BankAccountConnectionTooLongStep':
        return <BankAccountConnectionTooLongStep />
      case 'BaseRedirectStep':
      case 'VeriffRedirectStep':
      case 'SaltEdgeRedirectStep':
      case 'WebViewRedirectStep':
        return <RedirectStep />
      case 'CashAgreementsStep':
        return <CashAgreementsStep />
      case 'CashBankAccountEligibilityStep':
        return <CashBankAccountEligibilityStep />
      case 'CashBankAccountsListStep':
        return <CashBankAccountsListStep />
      case 'CashInstallmentsOfferStep':
        return <CashInstallmentsOfferStep />
      case 'CashCompletedStep':
        return <CashCompletedStep />
      case 'GeneralErrorStep':
        return <GeneralErrorStep />
      case 'InstallmentsAgreementsStep':
        return <InstallmentsAgreementsStep />
      case 'InstallmentsDisclaimerStep':
        return <InstallmentsDisclaimerStep />
      case 'OtpVerificationSmsStep':
        return <OtpVerificationSmsStep />
      case 'PaidPayIn3CompletedStep':
        return <PaidPayIn3CompletedStep />
      case 'SelectCashAmountStep':
        return <SelectCashAmountStep />
      case 'SingleInstallmentsOfferStep':
        return <SingleInstallmentsOfferStep />
      case 'AdditionalInfoIntroStep':
        return <AdditionalInfoIntroStep />
      case 'EmailPrivacyPolicyStep':
      case 'AccountExistsStep':
        return <EmailPrivacyPolicyStep />
      case 'AccountExistsWithoutPasswordStep':
        return <AccountExistsWithoutPasswordStep />
      case 'BankConnectionIntroStep':
        return <BankConnectionIntroStep />
      case 'PasswordStep':
        return <PasswordStep />
      case 'NameStep':
        return <NameStep />
      case 'OtpVerificationEmailStep':
        return <OtpVerificationEmailStep />
      case 'PhoneNumberStep':
        return <PhoneNumberStep />
      case 'PersonalIdStep':
        return <PersonalIdStep />
      case 'EducationTypeStep':
        return <EducationTypeStep />
      case 'ExpensesStep':
        return <ExpensesStep />
      case 'FamilyChildrenStep':
        return <FamilyChildrenStep />
      case 'FamilyStatusStep':
        return <FamilyStatusStep />
      case 'IncomeTypeStep':
        return <IncomeTypeStep />
      case 'EmploymentTypeStep':
        return <EmploymentTypeStep />
      case 'IncomeStep':
        return <IncomeStep />
      case 'IdentityVerificationIntroStep':
        return <IdentityVerificationIntroStep />
      case 'IdentityVerificationRetryStep':
        return <IdentityVerificationRetryStep />
      case 'CountryOfBirthStep':
        return <CountryOfBirthStep />
      case 'BankConnectionSuccessStep':
        return <BankConnectionSuccessStep />
      case 'ReviewIncomeStep':
        return <ReviewIncomeStep />
      case 'RiskCheckProcessingStep':
        return <RiskCheckProcessingStep />
      case 'RiskApprovedStep':
        return <RiskApprovedStep />
      case 'RiskManualReviewStep':
        return <RiskManualReviewStep />
      case 'PremiumAccountAgreementsStep':
        return <PremiumAccountAgreementsStep />
      case 'CzBasicAccountAgreementsStep':
        return <CzBasicAccountAgreementsStep />
      case 'PlBasicAccountAgreementsStep':
        return <PlBasicAccountAgreementsStep />
      case 'InternationalAddressStep':
        return <InternationalAddressStep />
      case 'LoanRepaymentsStep':
        return <LoanRepaymentsStep />
      case 'OnboardingRejectedStep':
        return <OnboardingRejectedStep />
      case 'OnboardingCompletedStep':
        return <OnboardingCompletedStep />
      case 'OnboardingErrorStep':
        return <OnboardingErrorStep />
      case 'CrossCheckWarningStep':
        return <CrossCheckWarningStep />
      case 'IncomeVerificationIntroStep':
        return <IncomeVerificationIntroStep />
      case 'IncomeVerificationCompletedStep':
        return <IncomeVerificationCompletedStep />
      case 'ProcessingStep':
        return <ProcessingStep />
      case 'IncomeVerificationBankAccountConnectionErrorStep':
        return <IncomeVerificationBankAccountConnectionErrorStep />
      case 'IncomeVerificationBankAccountConnectionTooLongStep':
        return <IncomeVerificationBankAccountConnectionTooLongStep />
      case 'IncomeVerificationBankAccountConnectionWarningStep':
        return <IncomeVerificationBankAccountConnectionWarningStep />
      case 'ConsentCollectionIntroStep':
        return <ConsentCollectionIntroStep />
      case 'ConsentCollectionCompletedStep':
        return <ConsentCollectionCompletedStep />
      default:
        throw new Error(`Unknown step: ${step}`)
    }
  }

  return (
    <FlowContext.Provider value={flowApi}>
      {submitting || loading || isNil(sessionId) ? <Loader /> : null}
      {!isNil(flow?.currentStep) && renderCurrentStep()}
    </FlowContext.Provider>
  )
}
